/**
 * Inside a world — the opened planet, filling the glass.
 *
 * The hero body, its claims laid along one road walked once per rung,
 * and the words the truth plane wrote for it.  Everything here is drawn
 * in the world's own frame (the camera has already flown to it), and
 * nothing here reads the registry: the reading arrived on the payload.
 */

import { Mastery, type CreditStyle } from "./Mastery";
import { PlanetBody } from "./PlanetBody";
import { claimTrail, laneOffset, trailPath } from "./geometry";
import { MONO, SERIF, type Palette, seasonColour } from "./palette";
import type { ClaimReading, PlanetReading } from "./types";

export function OpenPlanet({
  planet, slot, P, nss, gauge, credit, selClaim, onClaim, onClose,
}: {
  planet: PlanetReading;
  /** The orbit this world is drawn on, as the sky drew it — see the
   *  `slot` note in the planet loop.  The hero body has to be the same
   *  world the reader just tapped. */
  slot: number | null;
  P: Palette;
  nss: Record<string, unknown>;
  gauge: string[];
  credit: CreditStyle;
  selClaim: number | null;
  onClaim: (planetId: string, index: number) => void;
  onClose: () => void;
}) {
  const claims = planet.claims;
  const binding = (claim: ClaimReading) => claim.rungs.filter((rung) => rung.binding);
  const points = claimTrail(claims.length);
  const selected = selClaim === null ? null : claims[selClaim] ?? null;
  const lanes = claims.length ? binding(claims[0]).length : 0;
  const rungCount = Math.max(1, lanes);
  const colour = planet.state === "COLD" ? P.faint : seasonColour(P, planet.heat);

  /**
   * How far along the trail a given rung has been carried.
   *
   * A front, not an average: the road is walked in order, so the lane
   * fills up to the first claim that has not cleared this rung and stops
   * there.  That is what makes the drawn lane mean "how far you have
   * got" rather than "how much you have done somewhere".
   */
  const front = (rungIndex: number) => {
    if (!claims.length) return 0;
    let full = 0;
    for (let i = 0; i < claims.length; i += 1) {
      // PROOF, not credit: `progress` is measured from evidence alone,
      // while `met` can stand on a placement.  The road says how far the
      // learner has actually walked; the nodes carry the credit
      // rendering, where a gauge can draw the difference.
      const rung = binding(claims[i])[rungIndex];
      if (rung && rung.progress >= 1) full = i + 1;
      else break;
    }
    const partial = full < claims.length ? (binding(claims[full])[rungIndex]?.progress ?? 0) : 0;
    return Math.max(0, Math.min(1, (full + partial) / claims.length));
  };

  return (
    <g style={{ animation: "zfade 0.38s ease-out 0.2s both" }} data-testid="atlas-open-planet">
      <circle
        r="42" fill="transparent" onPointerDown={(e) => e.stopPropagation()}
        onClick={onClose} style={{ cursor: "zoom-out" }}
      />
      <g pointerEvents="none">
        <circle r="240" fill={P.bg} fillOpacity={P.night ? 0.82 : 0.88} />
        <PlanetBody planet={planet} slot={slot} s={42} P={P} nss={nss} forceWarm />
        <circle r="42" fill={P.night ? "#04060E" : "#1E1A10"} fillOpacity={P.night ? 0.42 : 0.2} />
        <circle r="43.5" fill="none" stroke={colour} strokeWidth="0.8" strokeOpacity="0.75" {...nss} />
      </g>

      <g pointerEvents="none">
        <text
          x="0" y="-34.5" textAnchor="middle"
          style={{
            fontFamily: MONO, fontSize: 2.3, letterSpacing: "0.14em", fill: "#FFF", opacity: 0.62,
            filter: "drop-shadow(0 0 1.4px rgba(0,0,0,0.9))",
          }}
        >
          {`${claims.length} CLAIMS · ${planet.state}${planet.claimsGranted ? ` · ${planet.claimsGranted} ON CREDIT` : ""}`}
        </text>
        {wrap(planet.hook || planet.dockQuestion, 38)
          .slice(0, 2)
          .map((line, i) => (
            <text
              key={i} x="0" y={-29.6 + i * 4.4} textAnchor="middle"
              style={{
                fontFamily: SERIF, fontStyle: "italic", fontSize: 3.5, fill: "#FFF", opacity: 0.95,
                filter: "drop-shadow(0 0 1.6px rgba(0,0,0,0.9))",
              }}
            >
              {line}
            </text>
          ))}
      </g>

      {/* one road, walked once per rung */}
      {[...Array(Math.min(rungCount, 3))].map((_, k) => {
        const lane = trailPath(laneOffset(points, (k - (Math.min(rungCount, 3) - 1) / 2) * 3));
        const f = front(k);
        return (
          <g key={k} pointerEvents="none">
            <path d={lane} fill="none" stroke="#FFFFFF" strokeOpacity="0.22" strokeWidth="0.55" strokeDasharray="1.1 1.7" />
            {f > 0.004 && (
              <path
                d={lane} fill="none" stroke={gauge[k % gauge.length]} strokeOpacity="0.97"
                strokeWidth="1.3" strokeLinecap="round" pathLength="100"
                strokeDasharray={`${(f * 100).toFixed(1)} 100`}
                style={{ filter: `drop-shadow(0 0 1.4px ${gauge[k % gauge.length]}AA)` }}
              />
            )}
          </g>
        );
      })}

      <g pointerEvents="none">
        {(claims.length ? binding(claims[0]) : []).slice(0, 3).map((rung, k) => (
          <text
            key={rung.name} x="-30" y={-1.4 + k * 4.4} textAnchor="end"
            style={{
              fontFamily: MONO, fontSize: 2.7, fill: gauge[k % gauge.length],
              opacity: front(k) > 0.004 ? 1 : 0.45,
              filter: "drop-shadow(0 0 1.4px rgba(0,0,0,0.9))",
            }}
          >
            {rung.name.toLowerCase()}
          </text>
        ))}
      </g>

      {claims.map((claim, index) => {
        const [nx, ny] = points[index] ?? [0, 0];
        const isSel = selClaim === index;
        return (
          <g
            key={claim.id} onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onClaim(planet.id, index)} style={{ cursor: "pointer" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClaim(planet.id, index);
              }
            }}
            role="button" tabIndex={0}
            aria-label={`${claim.title || claim.id} — ${claim.sentence}`}
            data-testid={`atlas-claim-${claim.id}`} data-claim-state={claim.state}
          >
            <circle cx={nx} cy={ny} r="10" fill="transparent" />
            <circle
              cx={nx} cy={ny} r="7.4" fill={P.night ? "#070B16" : "#FBF5E6"}
              stroke={isSel ? P.goldHi : claimInk(P, claim)} strokeOpacity={isSel ? 0.95 : 0.55}
              strokeWidth={isSel ? 0.6 : 0.45} {...nss}
            />
            {isSel && <circle cx={nx} cy={ny} r="9.1" fill="none" stroke={P.goldHi} strokeWidth="0.5" strokeOpacity="0.85" {...nss} />}
            {!isSel && planet.nextClaim === claim.id && (
              <circle
                cx={nx} cy={ny} r="8.8" fill="none" stroke={P.gold} strokeWidth="0.5"
                strokeOpacity="0.8" strokeDasharray="0.7 1.9" {...nss}
                data-testid="atlas-next-claim"
              />
            )}
            <g transform={`translate(${nx} ${ny})`}>
              <Mastery
                rungs={claim.rungs.filter((rung) => rung.binding)} R={4} colours={gauge} P={P}
                nss={nss} node credit={credit}
                idPrefix={`node-${claim.id.replace(/[^a-zA-Z0-9]/g, "_")}-`}
              />
            </g>
            {claim.granted && (
              <g pointerEvents="none">
                <circle cx={nx + 5.3} cy={ny - 5.3} r="1.9" fill={P.panel} stroke={P.gold} strokeWidth="0.55" strokeOpacity="0.7" />
                <circle cx={nx + 5.3} cy={ny - 5.3} r="0.6" fill={P.gold} fillOpacity="0.7" />
              </g>
            )}
            {claim.burned && (
              <g pointerEvents="none" style={{ animation: "zburn 1.1s ease-in-out infinite", transformOrigin: `${nx}px ${ny - 6}px` }}>
                <rect x={nx - 1.4} y={ny - 7.4} width="2.8" height="2.8" transform={`rotate(45 ${nx} ${ny - 6})`} fill={P.russet} />
              </g>
            )}
            <text
              x={nx} y={ny + 10.8} textAnchor="middle" pointerEvents="none"
              style={{
                fontFamily: MONO, fontSize: 2.5, fill: isSel ? P.goldHi : "#FFF",
                opacity: isSel ? 1 : 0.8, filter: "drop-shadow(0 0 1.4px rgba(0,0,0,0.9))",
              }}
            >
              {index + 1}
            </text>
          </g>
        );
      })}

      <g pointerEvents="none">
        {selected && (
          <text
            x="0" y="32.5" textAnchor="middle"
            style={{
              fontFamily: MONO, fontSize: 2.9, fill: "#FFF", opacity: 0.95,
              filter: "drop-shadow(0 0 1.6px rgba(0,0,0,0.9))",
            }}
          >
            {`${(selClaim ?? 0) + 1} · ${selected.title || selected.id}`}
          </text>
        )}
        <text
          x="0" y={selected ? 36.8 : 33.5} textAnchor="middle"
          style={{
            fontFamily: SERIF, fontStyle: "italic", fontSize: 2.6, fill: P.goldHi, opacity: 0.95,
            filter: "drop-shadow(0 0 1.6px rgba(0,0,0,0.9))",
          }}
        >
          {selected ? selected.sentence : planet.sentence}
        </text>
      </g>
    </g>
  );
}

/**
 * The ink a claim node is outlined in — its GRIP state, not its rung.
 *
 * The two say different things and the node has room for both: the
 * gauge inside says what has been SHOWN (monotone, permanent), the
 * outline says what is HELD TODAY (a condition that decays).  A claim
 * can be crossed and cooling at once, and this is where you see it.
 */
function claimInk(P: Palette, claim: ClaimReading): string {
  switch (claim.state) {
    case "BURNED":
      return P.russet;
    case "SHAKEN":
    case "SLIPPING":
      return P.sgold;
    case "UNSEEN":
      return P.faint;
    case "ASSUMED":
      return P.gold;
    case "COOLING":
      return P.sgold;
    default:
      return P.spring;
  }
}

/** Word-wrap for SVG text, which has no wrapping of its own. */
function wrap(text: string, width: number): string[] {
  const words = (text || "").split(" ").filter(Boolean);
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    if ((current + " " + word).trim().length > width) {
      if (current.trim()) lines.push(current.trim());
      current = word;
    } else {
      current += " " + word;
    }
  });
  if (current.trim()) lines.push(current.trim());
  return lines;
}

